import { InvalidCriteriaError, InvalidMethodInputError, MissingMethodParamError } from '../../errors'
import { type FailFn, MethodParams, parseSchema } from '../params'
import type { ZodSchema } from '../types'

// One invocation's `methodData`, and the two ways the runner reads it.
//
// A method's input is keyed by its declared param names (see the console's execution controller). SYSTEM
// methods carry a single well-known param, helper methods a `payload`. Pulling one out and validating it are
// separate failures on purpose:
//   • the param is absent          → MissingMethodParamError — the CALL is malformed
//   • the param's value is invalid → InvalidMethodInputError — the VALUE is malformed, with issue paths
//                                    relative to that param
// Criteria always fail as InvalidCriteriaError so the console can point at the dependent fields instead of
// reporting the whole call as broken; the two `invalid*` factories below are how a caller picks which.

export class MethodInput {
  constructor(
    readonly serviceId: string,
    readonly methodName: string,
    private readonly data: Record<string, unknown> = {}
  ) {}

  // Extract a declared param. Absent is loud rather than silently defaulted — a missing param means a
  // malformed call, and failing here surfaces it at the source.
  require<T>(param: string): T {
    const value = (this.data ?? {})[param]

    if (value == null) {
      throw new MissingMethodParamError(this.serviceId, param)
    }

    return value as T
  }

  /**
   * Extract a declared param and validate its value against the schema that owns it.
   *
   * An OBJECT schema — every runtime-owned wrapper (`callbackObject`, `invocation`, `payload`) — goes
   * through the same per-field pipeline every author-declared slot uses, so `.optional()` means one thing
   * everywhere: a blank arrives as absence whether the host sent `null`, `''` or nothing at all.
   *
   * That consistency is the point. While these wrappers were parsed by a bare `safeParse`, `.optional()`
   * rejected `null` here while accepting it everywhere else, and each field had to be remembered as
   * `.nullish()` the first time a host sent a null — which is how `state` on the OAuth callback stayed
   * broken for a non-PKCE provider, where the console legitimately has no verifier to hand back.
   *
   * `carryUnknown` preserves the passthrough these schemas declare with `z.looseObject`: a host may send
   * fields a given milestone does not model yet, and dropping them silently would be worse.
   */
  parseField<T>(param: string, schema?: ZodSchema): T {
    const value = this.require<unknown>(param)
    const fail = this.invalidInput(param)

    // A wrapper that is not an object at all fails as the WRAPPER, not field by field: iterating declared
    // fields over a string would report "«Search» must be a string" for a payload that was never an object.
    const isObject = !!value && typeof value === 'object' && !Array.isArray(value)

    if (!schema?.shape || !isObject) {
      return parseSchema<T>(schema, value, fail, param)
    }

    return MethodParams.of(schema).normalize<Record<string, unknown>>(
      value as Record<string, unknown>,
      fail,
      { carryUnknown: true }
    ) as T
  }

  // ── failure identities, for the slots a caller parses itself ────────────────

  // `target` names the slot that failed — a param name, or 'params' / 'triggerData' for the whole input.
  invalidInput(target: string): FailFn {
    return issues => {
      throw new InvalidMethodInputError(this.serviceId, this.methodName, target, issues)
    }
  }

  invalidCriteria(): FailFn {
    return issues => {
      throw new InvalidCriteriaError(this.serviceId, this.methodName, issues)
    }
  }
}
